/**
 * Utilidades para manejar estados de comunicaciones y rebotes de email
 */
import PropTypes from "prop-types";

// Tipos de estado de comunicación
export const COMMUNICATION_STATES = {
  PENDING: "pending",
  DELIVERED: "delivered",
  BOUNCED: "bounced",
  PARTIAL_DELIVERED: "partial_delivered",
};

/**
 * Obtiene el estado principal de una comunicación basado en sus destinatarios
 * @param {Object} comunicacion - Objeto comunicación con destinatarios y errores_envio
 * @returns {Object} Estado con tipo, ícono, color y tooltip
 */
export const getCommunicationStatus = (comunicacion) => {
  if (!comunicacion || !comunicacion.destinatarios) {
    return {
      type: COMMUNICATION_STATES.PENDING,
      icon: "⏳",
      color: "#ff9800", // Naranja
      tooltip: "Estado desconocido",
    };
  }

  const errores = comunicacion.errores_envio || [];
  const tieneRebotes = comunicacion.tiene_rebotes || false;
  const totalErrores = comunicacion.total_errores || 0;

  // Si hay rebotes específicos
  if (tieneRebotes) {
    const rebotes = errores.filter((error) => error.error_envio === "rebote");
    const totalDestinatarios = getTotalDestinarios(comunicacion.destinatarios);

    if (rebotes.length === totalDestinatarios) {
      // Todos los emails rebotaron
      return {
        type: COMMUNICATION_STATES.BOUNCED,
        icon: "🔴",
        color: "#f44336", // Rojo
        tooltip: `Todos los emails rebotaron (${rebotes.length})`,
      };
    } else if (rebotes.length > 0) {
      // Algunos emails rebotaron
      return {
        type: COMMUNICATION_STATES.PARTIAL_DELIVERED,
        icon: "🟠",
        color: "#ff9800", // Naranja
        tooltip: `${rebotes.length} de ${totalDestinatarios} emails rebotaron`,
      };
    }
  }

  // Si hay errores pero no rebotes
  if (totalErrores > 0) {
    return {
      type: COMMUNICATION_STATES.BOUNCED,
      icon: "❗",
      color: "#ff9800", // Naranja
      tooltip: `${totalErrores} errores de envío`,
    };
  }

  // Verificar si hay destinatarios notificados
  const estadoNotificacion = getNotificationStatus(comunicacion.destinatarios);

  if (estadoNotificacion.todosNotificados) {
    return {
      type: COMMUNICATION_STATES.DELIVERED,
      icon: "✅",
      color: "#4caf50", // Verde
      tooltip: "Todos los emails fueron entregados",
    };
  } else if (estadoNotificacion.algunosNotificados) {
    return {
      type: COMMUNICATION_STATES.PARTIAL_DELIVERED,
      icon: "🟡",
      color: "#ff9800", // Naranja
      tooltip: `${estadoNotificacion.notificados} de ${estadoNotificacion.total} emails entregados`,
    };
  } else {
    return {
      type: COMMUNICATION_STATES.PENDING,
      icon: "⏳",
      color: "#2196f3", // Azul
      tooltip: "Pendiente de envío o confirmación",
    };
  }
};

/**
 * Cuenta el total de destinatarios en el JSON de destinatarios
 * @param {Object} destinatarios - JSON de destinatarios
 * @returns {number} Total de destinatarios
 */
const getTotalDestinarios = (destinatarios) => {
  if (!destinatarios || typeof destinatarios !== "object") {
    return 0;
  }

  let total = 0;

  // Contar destinatarios individuales
  if (destinatarios.deudor?.email) total++;
  if (destinatarios.deudor_apoderado?.email) total++;

  // Contar arrays de destinatarios
  ["centrales_riesgo", "juzgados", "pagadores_libranza", "destinatarios_manuales"].forEach(
    (field) => {
      if (Array.isArray(destinatarios[field])) {
        total += destinatarios[field].filter((item) => item?.email).length;
      }
    }
  );

  // Contar acreedores (estructura compleja)
  if (Array.isArray(destinatarios.acreedores)) {
    destinatarios.acreedores.forEach((acreedor) => {
      if (Array.isArray(acreedor.correos)) {
        total += acreedor.correos.filter((correo) => correo?.email).length;
      }
      if (acreedor.apoderado?.email) total++;
    });
  }

  // Contar apoderados de acreedores
  if (Array.isArray(destinatarios.acreedores_apoderados)) {
    total += destinatarios.acreedores_apoderados.filter((item) => item?.email).length;
  }

  return total;
};

/**
 * Verifica el estado de notificación de los destinatarios
 * @param {Object} destinatarios - JSON de destinatarios
 * @returns {Object} Estado de notificación
 */
const getNotificationStatus = (destinatarios) => {
  if (!destinatarios || typeof destinatarios !== "object") {
    return { total: 0, notificados: 0, todosNotificados: false, algunosNotificados: false };
  }

  let total = 0;
  let notificados = 0;

  // Verificar destinatarios individuales
  if (destinatarios.deudor?.email) {
    total++;
    if (destinatarios.deudor.notificado) notificados++;
  }

  if (destinatarios.deudor_apoderado?.email) {
    total++;
    if (destinatarios.deudor_apoderado.notificado) notificados++;
  }

  // Verificar arrays de destinatarios
  ["centrales_riesgo", "juzgados", "pagadores_libranza", "destinatarios_manuales"].forEach(
    (field) => {
      if (Array.isArray(destinatarios[field])) {
        destinatarios[field].forEach((item) => {
          if (item?.email) {
            total++;
            if (item.notificado) notificados++;
          }
        });
      }
    }
  );

  // Verificar acreedores
  if (Array.isArray(destinatarios.acreedores)) {
    destinatarios.acreedores.forEach((acreedor) => {
      if (Array.isArray(acreedor.correos)) {
        acreedor.correos.forEach((correo) => {
          if (correo?.email) {
            total++;
            if (correo.notificado) notificados++;
          }
        });
      }
      if (acreedor.apoderado?.email) {
        total++;
        if (acreedor.apoderado.notificado) notificados++;
      }
    });
  }

  // Verificar apoderados de acreedores
  if (Array.isArray(destinatarios.acreedores_apoderados)) {
    destinatarios.acreedores_apoderados.forEach((apoderado) => {
      if (apoderado?.email) {
        total++;
        if (apoderado.notificado) notificados++;
      }
    });
  }

  return {
    total,
    notificados,
    todosNotificados: total > 0 && notificados === total,
    algunosNotificados: notificados > 0 && notificados < total,
  };
};

/**
 * Obtiene una lista detallada de errores de envío para mostrar en tooltips
 * @param {Array} erroresEnvio - Array de errores de envío
 * @returns {Array} Lista formateada de errores
 */
export const getErrorDetails = (erroresEnvio) => {
  if (!Array.isArray(erroresEnvio) || erroresEnvio.length === 0) {
    return [];
  }

  return erroresEnvio.map((error) => ({
    tipo: error.tipo,
    email: error.email,
    error: error.error_envio,
    descripcion: getErrorDescription(error.error_envio),
  }));
};

/**
 * Obtiene una descripción legible del tipo de error
 * @param {string} errorTipo - Tipo de error
 * @returns {string} Descripción del error
 */
const getErrorDescription = (errorTipo) => {
  const descripciones = {
    rebote: "Email rebotado",
    invalido: "Email inválido",
    bloqueado: "Email bloqueado",
    temporal: "Error temporal",
    spam: "Marcado como spam",
  };

  return descripciones[errorTipo] || "Error desconocido";
};

/**
 * Componente React para mostrar el ícono de estado de comunicación
 * @param {Object} comunicacion - Objeto comunicación
 * @param {Object} props - Props adicionales
 * @returns {JSX.Element} Componente ícono con tooltip
 */
export const CommunicationStatusIcon = ({ comunicacion, size = "24px", showTooltip = true }) => {
  const status = getCommunicationStatus(comunicacion);
  const errors = getErrorDetails(comunicacion.errores_envio || []);

  const iconStyle = {
    fontSize: size,
    color: status.color,
    cursor: showTooltip ? "help" : "default",
  };

  if (showTooltip) {
    return (
      <span
        style={iconStyle}
        title={status.tooltip}
        data-errors={errors.length > 0 ? JSON.stringify(errors) : null}
      >
        {status.icon}
      </span>
    );
  }

  return <span style={iconStyle}>{status.icon}</span>;
};

CommunicationStatusIcon.propTypes = {
  comunicacion: PropTypes.object.isRequired,
  size: PropTypes.string,
  showTooltip: PropTypes.bool,
};
